import type { ReactNode } from "react";
import { Link } from "react-router-dom";

// GM training guide for the Food Cost module. Static content: keep the
// button and tab names here in sync with the views they describe.

const USABLE_COUNTS: Array<[product: string, pack: string, enter: string]> = [
  ["Churros", "50 ct", "100 each (cut in half)"],
  ["Lemons", "165 ct", "150 each"],
  ["Limes", "200 ct", "180 each"],
  ["Oranges", "113 ct", "100 each"],
  ["Ketchup", "6/114 oz", "570 oz"],
  ["Chicken tenders", "2/5 lb", "10 lb"],
  ["Queso", "4/5 lb", "20 lb"],
  ["French fries", "6/5 lb", "30 lb"],
  ["Slider buns", "8/24", "192 each"],
  ["Brisket", "catch weight", "the lb on the invoice"],
];

const SECTIONS = [
  { id: "overview", label: "How it works" },
  { id: "upload", label: "Upload an invoice" },
  { id: "counts", label: "Usable counts" },
  { id: "menu", label: "Menu items" },
  { id: "review", label: "Check food cost" },
  { id: "weekly", label: "Weekly routine" },
] as const;

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section
      id={id}
      className="scroll-mt-4 space-y-3 rounded-lg border border-tribal-border bg-tribal-surface p-4 sm:p-5"
    >
      <h2 className="text-base font-semibold text-white">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-tribal-muted">{children}</div>
    </section>
  );
}

function Steps({ children }: { children: ReactNode }) {
  return <ol className="list-decimal space-y-2 pl-5 marker:text-tribal-muted">{children}</ol>;
}

function Bullets({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-1.5 pl-5 marker:text-tribal-muted">{children}</ul>;
}

function Ui({ children }: { children: ReactNode }) {
  return <span className="font-medium text-white">{children}</span>;
}

function TabLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={`/food/${to}`} className="font-medium text-white underline decoration-tribal-red underline-offset-2">
      {children}
    </Link>
  );
}

export function HelpView() {
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold text-white">Food Cost training guide</h1>
        <p className="text-sm text-tribal-muted">
          The GM's job here is to enter every US Foods invoice and keep menu costs accurate. That's
          about 15 minutes a week once you know the steps.
        </p>
        <nav aria-label="Guide sections" className="flex flex-wrap gap-1.5 pt-1">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" });
              }}
              className="rounded-md border border-tribal-border px-2.5 py-1 text-xs font-medium text-tribal-muted hover:bg-tribal-border hover:text-white"
            >
              {s.label}
            </a>
          ))}
        </nav>
      </div>

      <Section id="overview" title="How it works">
        <p>
          Every cost comes from invoices: <Ui>invoice → ingredient cost → menu item cost → food cost %</Ui>.
        </p>
        <Steps>
          <li>Each invoice line is linked to an ingredient, e.g. "CHICKEN, TNDLN BTRD BRDD MED" → Chicken Tenders.</li>
          <li>
            An ingredient's cost comes from its <Ui>newest</Ui> invoice: line total ÷ quantity.
          </li>
          <li>Menu items are built from ingredients, so their cost updates with every invoice.</li>
          <li>
            Food cost % = recipe cost ÷ menu price. Food is on target at 30% or less and over target above
            35%. Drinks use 22% and 28%.
          </li>
        </Steps>
        <p>
          A wrong link or wrong quantity on one invoice line makes every menu item that uses that
          ingredient show the wrong cost.
        </p>
      </Section>

      <Section id="upload" title="Upload a US Foods invoice">
        <p>Do this on delivery day. About 5 minutes per invoice.</p>
        <Steps>
          <li>Download the invoice PDF from US Foods (order.usfoods.com or the emailed copy).</li>
          <li>
            Open the <TabLink to="upload">Upload</TabLink> tab, choose the PDF (a clear photo, CSV or
            XLSX also works) and click <Ui>Upload &amp; Extract</Ui>.
          </li>
          <li>
            Set <Ui>Vendor</Ui> to <Ui>US Foods, Inc.</Ui> Past matches only come from invoices filed
            under that vendor, so don't create a new one.
          </li>
          <li>Check the <Ui>Invoice Date</Ui> matches the PDF.</li>
          <li>
            Click <Ui>Apply historical mappings</Ui>. Items we've bought before get their ingredient and
            usable quantity filled in, with an "exact" or "fuzzy" match note under the description. The
            quantity already counts the cases on this invoice: 2 cases of tenders shows 20 lb, and
            brisket shows its invoice weight.
          </li>
          <li>
            Check the <Ui>Quantity</Ui> column against the usable counts below and fix any that are off.
            New items start at whatever the invoice said (often "1 case"), so change them to the usable
            amount and unit, e.g. 30 lb.
          </li>
          <li>
            Go down the <Ui>Ingredient Mapping</Ui> column:
            <Bullets>
              <li>Fuzzy matches: confirm the ingredient is right.</li>
              <li>
                New food items: pick the ingredient, or click <Ui>+</Ui> to create one.
              </li>
              <li>Supplies (towels, gloves, sanitizer, shortening): leave as <Ui>Unmapped</Ui>.</li>
            </Bullets>
          </li>
          <li>
            Click <Ui>Save Invoice</Ui>.
          </li>
        </Steps>
        <p>
          <Ui>Don't upload</Ui> credit memos (rebates with no products) or an invoice that's already on
          the <TabLink to="invoices">Invoices</TabLink> tab.
        </p>
      </Section>

      <Section id="counts" title="Usable counts">
        <p>
          The quantity on an invoice line is what the price gets divided by, so it has to be what we
          actually use. We take roughly 10% off for waste, and churros count double because they're cut
          in half.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-tribal-muted">
              <tr className="border-b border-tribal-border">
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">Printed pack</th>
                <th className="px-3 py-2">Usable per case</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tribal-border/60">
              {USABLE_COUNTS.map(([product, pack, enter]) => (
                <tr key={product}>
                  <td className="px-3 py-2 text-white">{product}</td>
                  <td className="px-3 py-2">{pack}</td>
                  <td className="px-3 py-2">{enter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Bullets>
          <li>
            More than one case: multiply. 2 cases of tenders = 20 lb. Historical mappings do this for
            you; new items need it typed in.
          </li>
          <li>
            Sanity check: after saving, an ingredient's <Ui>Latest Cost</Ui> on the{" "}
            <TabLink to="ingredients">Ingredients</TabLink> tab should be close to last week's. A cost
            that halves or doubles usually means a wrong quantity.
          </li>
          <li>
            Batch ingredients (Pico de Gallo, Killshot Sauce, Bacon Jam) are house recipes. They're
            costed from their components and never appear on invoices.
          </li>
        </Bullets>
      </Section>

      <Section id="menu" title="Build and update menu items">
        <Steps>
          <li>
            On the <TabLink to="menu">Menu</TabLink> tab, click <Ui>Add Menu Item</Ui>, or open an
            existing item to change it.
          </li>
          <li>Enter the name, category and menu price.</li>
          <li>
            In the <Ui>Recipe Builder</Ui>, add every ingredient on the plate with its portion, and pick
            the right unit. The unit starts as "each", so change it for anything weighed (e.g. Chicken
            Tenders 6 oz).
          </li>
          <li>
            Leave <Ui>Est. Cost</Ui> blank. Anything typed there overrides the recipe cost and stops the
            item updating from invoices.
          </li>
          <li>Save.</li>
        </Steps>
        <p>If the kitchen changes a portion, update the recipe the same week.</p>
      </Section>

      <Section id="review" title="Check food cost">
        <Bullets>
          <li>
            <TabLink to="">Dashboard</TabLink>: <Ui>Average Food Cost %</Ui> and{" "}
            <Ui>Items Over Target</Ui> each week, and Recent Invoices to confirm the last delivery is in.
          </li>
          <li>
            <TabLink to="analysis">Analysis</TabLink>: every active item with its food cost %, status
            and a <Ui>Suggested Price</Ui>.
          </li>
          <li>
            <TabLink to="ingredients">Ingredients</TabLink>: open one to see its cost and which menu
            items use it.
          </li>
        </Bullets>
        <p>
          An item over target is usually a data mistake first: check the invoice quantity and the recipe
          portion. If both are right, take it to the owner with the suggested price or a portion change.
        </p>
      </Section>

      <Section id="weekly" title="Weekly routine">
        <Steps>
          <li>Upload each US Foods invoice on delivery day.</li>
          <li>Map any new food items and set their usable quantity.</li>
          <li>Check Latest Cost on ingredients that changed.</li>
          <li>Review the Dashboard: average food cost % and items over target.</li>
          <li>Flag anything over target to the owner.</li>
        </Steps>
      </Section>
    </div>
  );
}
